import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import {
  Box,
  Typography,
  Paper,
  Alert,
  Button,
  CircularProgress,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Chip,
  Stack,
} from "@mui/material";

import theme from "./theme";
import NavBar from "./components/NavBar";
import { isAdmin, isImpersonating, whoAmI, getToken } from "./auth/token";
import BACKEND_URL from "./config";

// Helper to include JWT on requests
function authHeaders() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function Admin() {
  const navigate = useNavigate();

  // Client-side admin gate (backend is protected too)
  const admin = useMemo(() => isAdmin(), []);
  const impersonating = useMemo(() => isImpersonating(), []);
  const me = useMemo(() => whoAmI(), []);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [opError, setOpError] = useState("");

  async function fetchUsers() {
    try {
      setLoading(true);
      setOpError("");
      const res = await fetch(`${BACKEND_URL}/api/admin/users`, {
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setUsers(data);
    } catch (err) {
      console.error("Error fetching users", err);
      setOpError(err.message || "Failed to load users.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (admin) fetchUsers();
  }, [admin]);

  async function handleToggleAdmin(email, nextValue) {
    setOpError("");
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/users/${encodeURIComponent(email)}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
        body: JSON.stringify({ is_admin: !!nextValue }),
      });

      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.message || `HTTP ${res.status}`);
      }

      // Refresh list to reflect change
      await fetchUsers();
    } catch (err) {
      console.error("Error toggling admin", err);
      setOpError(err.message || "Failed to update user.");
    }
  }

  async function handleLoginAs(email) {
    setOpError("");
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/login-as`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
        body: JSON.stringify({ email }),
      });

      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.message || `HTTP ${res.status}`);
      }

      // Save the impersonation token and reload the app in that context
      localStorage.setItem("access_token", body.access_token);
      // Tip: you could navigate to a safer landing page for non-admins
      window.location.replace("/"); // full reload to reset app state with new claims
    } catch (err) {
      console.error("Error impersonating user", err);
      setOpError(err.message || "Failed to log in as that user.");
    }
  }

  return (
    <ThemeProvider theme={theme}>
      <NavBar />

      <Box sx={{ p: 2, maxWidth: 1200, mx: "auto" }}>
        <Typography variant="h4" sx={{ mb: 2 }}>
          Admin
        </Typography>

        {!admin ? (
          <Paper sx={{ p: 2 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              Admins only. You don’t have access to this page.
            </Alert>
            <Button variant="outlined" onClick={() => navigate("/")}>
              Go to Home
            </Button>
          </Paper>
        ) : (
          <>
            {/* Session context banner */}
            <Paper sx={{ p: 2, mb: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Chip label={`You: ${me?.email || "unknown"}`} />
                {impersonating && me?.impersonated_by && (
                  <Chip
                    color="warning"
                    label={`Impersonating (started by ${me.impersonated_by})`}
                  />
                )}
                {me?.organization_id && (
                  <Chip variant="outlined" label={`Org: ${me.organization_id}`} />
                )}
              </Stack>
            </Paper>

            {opError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {opError}
              </Alert>
            )}

            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Users
              </Typography>

              {loading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={22} />
                  <Typography>Loading users…</Typography>
                </Box>
              ) : users.length === 0 ? (
                <Typography>No users found.</Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Email</TableCell>
                        <TableCell>Admin?</TableCell>
                        <TableCell>Organization</TableCell>
                        <TableCell>OpenAI Vector Store</TableCell>
                        <TableCell>Background File</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {users.map((u) => {
                        const isAdminUser = !!u.is_admin;
                        const isMe = me?.email && me.email === u.email;

                        return (
                          <TableRow key={u.email}>
                            <TableCell>{u.email}</TableCell>
                            <TableCell>{isAdminUser ? "Yes" : "No"}</TableCell>
                            <TableCell>{u.organization_id || "-"}</TableCell>
                            <TableCell>
                              {u.organization_openai_vector_store_id || "-"}
                            </TableCell>
                            <TableCell>
                              {u.organization_background_info_openai_file_id || "-"}
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={1} flexWrap="wrap">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => handleToggleAdmin(u.email, !isAdminUser)}
                                  disabled={isMe} // optional: prevent de-adminning yourself via UI
                                  title={
                                    isMe
                                      ? "You cannot change your own admin status here."
                                      : isAdminUser
                                      ? "Set as non-admin"
                                      : "Set as admin"
                                  }
                                >
                                  {isAdminUser ? "Revoke admin" : "Make admin"}
                                </Button>

                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => handleLoginAs(u.email)}
                                  title="Get a token as this user and reload the app"
                                >
                                  Log in as user
                                </Button>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </>
        )}
      </Box>
    </ThemeProvider>
  );
}

